/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * UpdateDocs — manages all employee document uploads in one view.
 *
 * Data flow:
 * 1. GET api/documentNames/list   — required document types (templates)
 * 2. GET api/upload-documents     — already-uploaded files
 * 3. Match templates → uploaded docs by documentName
 * 4. Each card independently:
 * • Has existing file  → shows preview + "Update Document"
 * → PUT api/upload-documents/{id}  (field: file)
 * • No file yet        → shows upload zone
 * → POST api/upload-documents/upload (field: docName)
 *
 * Storage-type detection (S3 vs Drive): Eagerly fetches storage type upon file selection. 
 * If S3, it fetches presigned URL, uploads directly to S3, and submits the resulting key on form submission.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaCloudUploadAlt,
  FaTimesCircle,
  FaRedo,
  FaTimes,
  FaCheck,
  FaEye,
  FaLock,
  FaEdit,
  FaRegTrashAlt,
} from "react-icons/fa";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";

// Dynamic, admin-configured upload policy (allowed formats + size) for employee
// documents — GET api/upload-config/DOCUMENT. Replaces the hardcoded rules.
import { useUploadConfig, UseUploadConfigResult } from "hooks/useUploadConfig";

import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import MediaPreview, {
  MediaThumbnail,
  useMediaPreview,
  type MediaItem,
} from "components/MediaPreview";

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

interface DocTemplate {
  id: number;
  name: string;
  // From api/documentNames/list. `optional: false` ⇒ the document is REQUIRED
  // (mandatory); `optional: true` ⇒ it may be skipped. Undefined on older APIs.
  optional?: boolean;
  tempDeleted?: boolean;
}

interface UploadedDoc {
  id: number;
  documentName: string;
  url?: string;
  s3Url?: string;
  isVerified?: boolean;
  // Raw verification status from the API (e.g. "PENDING" | "VERIFIED" | "REJECTED").
  // Used to decide when a user may delete their own document (only while PENDING).
  verificationStatus?: string | null;
  // Admin's reason when the document was REJECTED — shown to the user on the card.
  rejectionReason?: string | null;
  fileType?: string | null;
  size?: string | null;
  uploadedAt?: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS & HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

// Named fallback limits — avoid magic numbers and keep KB/MB in sync by deriving
// the KB value instead of hardcoding it.
const KB_PER_MB = 1024;
const FALLBACK_MAX_FILE_SIZE_MB = 10;
const FALLBACK_MAX_FILES = 1;
const FALLBACK_ALLOWED_FILE_TYPES = ["PDF", "JPEG", "PNG", "JFIF"];

// Fallback upload policy used only while the admin-config call is in flight or
// if it fails. Live rules come from GET api/upload-config/DOCUMENT.
const DOCUMENT_UPLOAD_FALLBACK = {
  allowedFileTypes: FALLBACK_ALLOWED_FILE_TYPES,
  maxFiles: FALLBACK_MAX_FILES,
  maxFileSizeKb: FALLBACK_MAX_FILE_SIZE_MB * KB_PER_MB,
  maxFileSizeMb: FALLBACK_MAX_FILE_SIZE_MB,
};

function isPDFFile(file: File): boolean {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

function getDocUrl(doc: UploadedDoc): string {
  return doc.s3Url || doc.url || "";
}

/** Normalises the GET api/upload-documents response into a name → doc map */
function buildDocMap(data: any): Record<string, UploadedDoc> {
  const map: Record<string, UploadedDoc> = {};
  const items: UploadedDoc[] = Array.isArray(data)
    ? data
    : Object.values(data).map((v: any, i: number) => ({
      id: v.id ?? i,
      documentName: v.documentName ?? "Document",
      url: v.url ?? "",
      s3Url: v.s3Url ?? "",
      isVerified: v.isVerified,
      verificationStatus: v.verificationStatus ?? null,
      rejectionReason: v.rejectionReason ?? null,
      fileType: v.fileType ?? null,
      size: v.size ?? null,
      uploadedAt: v.uploadedAt ?? null,
    }));

  items.forEach((doc) => {
    if (doc.documentName) map[doc.documentName] = doc;
  });
  return map;
}

/** Format the API's ISO uploadedAt into a short readable date (empty if absent). */
function formatUploadedAt(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Shared classes for the card footer action buttons
const FOOTER_BTN =
  "flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
const FOOTER_NEUTRAL =
  "border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700";
const FOOTER_PRIMARY = "bg-blue-600 hover:bg-blue-700 text-white shadow-sm";

// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENT: DocCard
// ═══════════════════════════════════════════════════════════════════════════════

interface DocCardProps {
  template: DocTemplate;
  existing: UploadedDoc | undefined;
  onUploaded: () => void;
  // Shared upload policy fetched once by the parent (avoids one API call per card).
  uploadPolicy: UseUploadConfigResult;
}

function DocCard({ template, existing, onUploaded, uploadPolicy }: Readonly<DocCardProps>) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // S3 Specific states
  const [s3Key, setS3Key] = useState<string | null>(null);
  const [isUploadingToS3, setIsUploadingToS3] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Delete-confirmation modal + in-flight flag for the pending-document delete.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { addToast } = useToaster();

  const { previewProps, open: openPreview } = useMediaPreview();
  const hasExisting = !!existing;

  // Verification state is driven by the API's `verificationStatus`
  // ("APPROVED" | "PENDING" | "REJECTED"). The legacy `isVerified` boolean is only
  // a fallback for older responses that don't send a status string.
  const verificationStatus = (existing?.verificationStatus || "").toUpperCase();
  const isApproved =
    hasExisting &&
    (verificationStatus === "APPROVED" ||
      verificationStatus === "VERIFIED" ||
      (!verificationStatus && !!existing?.isVerified));
  // Rejected documents surface the admin's reason and prompt a re-upload.
  const isRejected = hasExisting && verificationStatus === "REJECTED";
  // Anything not yet approved or rejected is still pending.
  const isPending = hasExisting && !isApproved && !isRejected;
  const rejectionReason = existing?.rejectionReason?.trim() || "";

  // Approved documents are locked (can no longer be replaced or deleted). A user
  // may delete their own document only while it is still PENDING.
  const locked = isApproved;
  const canModify = !locked;

  const existingMediaItem: MediaItem | null = existing
    ? { url: getDocUrl(existing), name: existing.documentName, mimeType: existing.fileType ?? undefined }
    : null;

  // Create object URL for image preview; revoke on file change or unmount
  useEffect(() => {
    if (selectedFile && !isPDFFile(selectedFile)) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  const clearSelection = () => {
    setSelectedFile(null);
    setS3Key(null);
    setUploadError("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void validateAndSet(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canModify) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void validateAndSet(file);
  };

  const validateAndSet = async (file: File) => {
    // Validate against the admin-configured policy (allowed formats + size).
    const error = uploadPolicy.validateFile(file);
    if (error) {
      addToast(error, "error");
      return;
    }

    setSelectedFile(file);
    setUploadError("");
    setS3Key(null);

    setIsUploadingToS3(true);
    try {
      // 1. Fetch Storage Type dynamically upon selection
      const res = await apiService.get("api/admin/storage").catch(() => ({ data: { storage: "DRIVE" } }));
      const currentStorage = res?.data?.storage || "DRIVE";

      // 2. If S3, execute Eager Upload
      if (currentStorage === "S3") {
        const uploadUrlRes = await apiService.post("api/upload-url", { fileName: file.name });
        const { uploadUrl, key } = uploadUrlRes.data || {};

        if (!uploadUrl || !key) throw new Error("Invalid presigned URL response");

        const putRes = await fetch(uploadUrl, { method: "PUT", body: file });
        if (!putRes.ok) throw new Error("S3 upload failed");

        setS3Key(key);
      }
    } catch (err) {
      console.error("Storage/Upload Error:", err);
      setUploadError("Upload failed, please re-select");
    } finally {
      setIsUploadingToS3(false);
    }
  };

  const openFilePicker = () => {
    if (isSubmitting || isUploadingToS3 || !canModify) return;
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!selectedFile || isSubmitting || isUploadingToS3 || uploadError) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      // If s3Key exists, we know it's S3, append the key string instead of the raw file
      if (s3Key) {
        // S3 Upload
        formData.append("key", s3Key);
      } else {
        // Drive Upload
        if (hasExisting) {
          formData.append("file", selectedFile);
        } else {
          formData.append(template.name, selectedFile);
        }
      }

      // API Request
      if (hasExisting && existing) {
        await apiService.put(`api/upload-documents/${existing.id}`, formData);
        addToast(`${template.name} updated successfully`, "success");
      } else {
        await apiService.post("api/upload-documents/upload", formData);
        addToast(`${template.name} uploaded successfully`, "success");
      }

      clearSelection();
      onUploaded();
    } catch (err: any) {
      addToast(err?.response?.data?.message || err?.message || "Upload failed. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a pending document, then refresh so the card reverts to "Not Uploaded".
  const handleDelete = async () => {
    if (!existing || isDeleting) return;
    setIsDeleting(true);
    try {
      await apiService.delete(`api/upload-documents/${existing.id}`);
      addToast(`${template.name} deleted successfully`, "success");
      setShowDeleteConfirm(false);
      onUploaded();
    } catch (err: any) {
      addToast(
        err?.response?.data?.message || err?.message || "Delete failed. Please try again.",
        "error",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const isSelectedPdf = selectedFile ? isPDFFile(selectedFile) : false;
  const sizeMb = selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : "";

  const statusBadge = existing ? (
    isApproved ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 shrink-0">
        <FaCheckCircle size={8} /> Verified
      </span>
    ) : isRejected ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 shrink-0">
        <FaTimesCircle size={8} /> Rejected
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 shrink-0">
        <FaClock size={8} /> Pending
      </span>
    )
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 shrink-0">
      Not Uploaded
    </span>
  );

  // Required vs Optional tag, driven by the template's `optional` flag. Shown
  // beside the document name so the user always knows which documents are
  // mandatory, regardless of upload status. Hidden when the flag is absent.
  const requirementBadge =
    template.optional === false ? (
      <span
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300 shrink-0"
        title="This document is required"
      >
        Required
        {/* <span aria-hidden="true">*</span> Required */}
      </span>
    ) : template.optional === true ? (
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 shrink-0"
        title="This document is optional"
      >
        Optional
      </span>
    ) : null;

  const metaLine = selectedFile ? (
    <p
      className={`mt-1 text-[11px] font-medium truncate ${uploadError ? "text-red-500" : "text-blue-600 dark:text-blue-400"}`}
      title={uploadError || selectedFile.name}
    >
      {uploadError || `${isSelectedPdf ? "PDF" : "Image"} · ${selectedFile.name} · ${sizeMb} MB`}
    </p>
  ) : hasExisting ? (
    <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500 truncate">
      {[existing?.fileType?.split("/")[1]?.toUpperCase(), existing?.size, formatUploadedAt(existing?.uploadedAt)]
        .filter(Boolean)
        .join(" · ") || "Uploaded"}
    </p>
  ) : (
    <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
      {/* Don't show any rules until the server policy has loaded (or failed, in
          which case the fallback applies). Formats + size only — each card is a
          single document, so the file-count part of the policy isn't shown. */}
      {uploadPolicy.loaded
        ? `${uploadPolicy.allowedTypesLabel || "Any file"}${uploadPolicy.maxFileSizeMb ? ` · Max ${uploadPolicy.maxFileSizeMb} MB` : ""}`
        : "Loading allowed formats…"}
    </p>
  );

  return (
    <div
      className={`relative flex flex-col gap-3 bg-white dark:bg-gray-800 rounded-2xl border p-4 transition-all duration-200
        ${isDragging
          ? "border-blue-400 ring-2 ring-blue-200 dark:ring-blue-800"
          : "border-gray-100 dark:border-gray-700 hover:shadow-md"
        }`}
      onDragOver={(e) => { if (!canModify) return; e.preventDefault(); setIsDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
      onDrop={handleDrop}
    >
      <div className="flex items-center gap-4">
        {selectedFile ? (
          <div className="relative shrink-0 w-28 h-28 rounded-xl overflow-hidden border-2 border-blue-300 dark:border-blue-600 shadow-sm">
            {isSelectedPdf ? (
              <div className="w-full h-full bg-blue-50 dark:bg-blue-900/30 flex flex-col items-center justify-center gap-1.5">
                <FaFileAlt size={26} className="text-blue-500 dark:text-blue-400" />
                <span className="text-[11px] font-semibold text-blue-500 dark:text-blue-400">PDF</span>
              </div>
            ) : previewUrl ? (
              <img src={previewUrl} alt="Selected file preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <FaFileAlt size={26} className="text-gray-400 dark:text-gray-500" />
              </div>
            )}
            {/* Show loading spinner over thumbnail if uploading to S3 */}
            {isUploadingToS3 ? (
              <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 flex items-center justify-center backdrop-blur-[1px]">
                <span className="w-6 h-6 rounded-full border-2 border-blue-500/30 border-t-blue-600 animate-spin" />
              </div>
            ) : (
              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-blue-600 text-white text-[9px] font-bold tracking-wide shadow">
                NEW
              </span>
            )}
          </div>
        ) : hasExisting && existingMediaItem ? (
          <MediaThumbnail
            item={existingMediaItem}
            onClick={() => openPreview([existingMediaItem], 0)}
            size="lg"
            className="shrink-0 !w-28 !h-28 shadow-sm"
          />
        ) : (
          <button
            type="button"
            onClick={openFilePicker}
            disabled={isSubmitting || isUploadingToS3}
            className="shrink-0 w-28 h-28 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 flex flex-col items-center justify-center gap-1.5 transition-colors disabled:cursor-not-allowed"
            title="Upload document"
          >
            <FaCloudUploadAlt
              size={26}
              className={isDragging ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}
            />
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Upload</span>
          </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3
              className="min-w-0 text-sm font-bold text-gray-900 dark:text-white truncate leading-snug"
              title={template.name}
            >
              {template.name}
            </h3>
            {requirementBadge}
          </div>
          <div className="mt-1.5">{statusBadge}</div>
          {metaLine}
          {locked && !selectedFile && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
              <FaLock size={8} /> Locked after verification
            </p>
          )}
          {/* Rejection reason from the admin — tells the user why it was rejected
              and prompts a re-upload. Hidden while a new file is being selected.
              Clamped to 2 lines so a long reason can't stretch the card and break
              the grid alignment; the full text shows on hover via the title tooltip. */}
          {isRejected && rejectionReason && !selectedFile && (
            <div
              className="mt-1.5 flex items-start gap-1.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 px-2 py-1"
              title={rejectionReason}
            >
              <FaTimesCircle size={10} className="mt-[2px] shrink-0 text-red-500 dark:text-red-400" />
              <p className="min-w-0 text-[10px] font-medium text-red-600 dark:text-red-400 leading-snug line-clamp-2 break-words">
                <span className="font-semibold">Rejected:</span> {rejectionReason}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700/60">
        {selectedFile ? (
          <>
            <button
              type="button"
              onClick={clearSelection}
              disabled={isSubmitting || isUploadingToS3}
              className={`${FOOTER_BTN} ${FOOTER_NEUTRAL}`}
            >
              <FaTimes size={11} /> Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || isUploadingToS3 || !!uploadError}
              className={`${FOOTER_BTN} ${FOOTER_PRIMARY}`}
            >
              {isSubmitting || isUploadingToS3 ? (
                <>
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  {isUploadingToS3 ? "Uploading…" : (hasExisting ? "Updating…" : "Saving…")}
                </>
              ) : (
                <>
                  <FaCheck size={11} /> {hasExisting ? "Confirm Update" : "Confirm Upload"}
                </>
              )}
            </button>
          </>
        ) : hasExisting ? (
          <>
            <button
              type="button"
              onClick={() => existingMediaItem && openPreview([existingMediaItem], 0)}
              className={`${FOOTER_BTN} ${FOOTER_NEUTRAL}`}
            >
              <FaEye size={11} /> Preview
            </button>
            {canModify ? (
              <button
                type="button"
                onClick={openFilePicker}
                disabled={isSubmitting || isUploadingToS3}
                className={`${FOOTER_BTN} ${FOOTER_PRIMARY}`}
              >
                <FaEdit size={11} /> Replace
              </button>
            ) : (
              <div
                className={`${FOOTER_BTN} bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 cursor-default`}
                title="Verified — this document can no longer be changed"
              >
                <FaLock size={11} /> Verified
              </div>
            )}
            {/* Delete is only offered while the document is PENDING — a verified
                document stays locked. Compact icon button so Preview/Replace keep
                their prominence. */}
            {isPending && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting || isUploadingToS3 || isDeleting}
                className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Delete document"
                aria-label={`Delete ${template.name}`}
              >
                <FaRegTrashAlt className="text-error group-hover:scale-110 transition-transform" />

              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={openFilePicker}
            disabled={isSubmitting || isUploadingToS3}
            className={`${FOOTER_BTN} ${FOOTER_PRIMARY}`}
          >
            <FaCloudUploadAlt size={13} /> Upload Document
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={uploadPolicy.accept}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Delete confirmation — reuses the shared GenericModal + DeletePopup. */}
      {showDeleteConfirm && existing && (
        <GenericModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          header="Delete Document"
          size="md"
        >
          <DeletePopup
            selectedRow={existing}
            handleDelete={handleDelete}
            onClose={() => setShowDeleteConfirm(false)}
            message={
              <>
                Are you sure you want to delete{" "}
                {/* The name is the one word that decides whether this is the
                    right row, so it carries the weight. Quotes dropped — the
                    emphasis already sets it apart, and matches the delete
                    dialog in DocumentList. */}
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {template.name}
                </span>
                ? This action cannot be undone.
              </>
            }
          />
        </GenericModal>
      )}

      <MediaPreview {...previewProps} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function UpdateDocs() {
  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, UploadedDoc>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Fetch the admin-configured document upload policy ONCE for the whole page and
  // share it with every card — avoids one GET api/upload-config/DOCUMENT per card.
  const uploadPolicy = useUploadConfig("DOCUMENT", DOCUMENT_UPLOAD_FALLBACK);

  const fetchData = useCallback(async (isSilent = false) => {
    // Only show the full-page loader on the initial fetch or manual retries
    if (!isSilent) {
      setLoading(true);
    }
    setError(null);
    try {
      const [templatesRes, docsRes] = await Promise.all([
        apiService.get("api/documentNames/list"),
        apiService.get("api/upload-documents"),
      ]);

      const validTemplates: DocTemplate[] = (templatesRes.data || []).filter(
        (d: DocTemplate) => !d.tempDeleted
      );

      setTemplates(validTemplates);
      setUploadedDocs(buildDocMap(docsRes.data));
    } catch (err) {
      console.error("Failed to load document data:", err);
      setError("Failed to load documents. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData(); // Initial load defaults to isSilent = false
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Loading documents…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <FaTimesCircle size={24} className="text-red-400" />
        </div>
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Failed to load documents
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 max-w-xs">
          {error}
        </p>
        <button
          type="button"
          onClick={() => void fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
        >
          <FaRedo size={11} />
          Try Again
        </button>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center mb-4">
          <FaFileAlt size={22} className="text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          No document types configured
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-xs">
          Contact your administrator to configure required document types.
        </p>
      </div>
    );
  }

  const uploadedCount = templates.filter((t) => !!uploadedDocs[t.name]).length;
  const progressPercent = Math.round((uploadedCount / templates.length) * 100);
  console.log(`Uploaded ${uploadedCount} of ${templates.length} documents (${progressPercent}%)`);
  // Required documents (optional === false) still missing — surfaced so the user
  // knows what they must still upload.
  const requiredMissingCount = templates.filter(
    (t) => t.optional === false && !uploadedDocs[t.name],
  ).length;

  console.log(`Required documents still missing: ${requiredMissingCount}`);

  return (
    <div className="space-y-3">

      <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-3 gap-3 mt-3">
        {templates.map((template) => (
          <DocCard
            key={template.id}
            template={template}
            existing={uploadedDocs[template.name]}
            onUploaded={() => void fetchData(true)}
            uploadPolicy={uploadPolicy}
          />
        ))}
      </div>
    </div>
  );
}